/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useId } from 'react';
import { Tooltip, makeStyles } from '@material-ui/core';
import { PillarUnmeasuredReason } from '../../api/types';

/**
 * What the qualifier means, per reason (feature 0037). The chip LABEL is the
 * same either way — the reader's question is "how much of Security does this
 * number cover?", and the answer is "code only" whichever way the cloud areas
 * dropped out. Which way it was belongs in the explanation, not in a second
 * label nobody can tell apart at a glance.
 */
const EXPLANATION: Record<PillarUnmeasuredReason, string> = {
  'no-production-accounts':
    'Scored from code security only — this System curates no production AWS account, so its cloud posture is not scored.',
  'no-data':
    'Scored from code security only — no cloud posture findings have been collected for this System.',
};

const LABEL = 'code only';

const useStyles = makeStyles(theme => ({
  chip: {
    display: 'inline-block',
    padding: theme.spacing(0.125, 0.75),
    borderRadius: '0.75rem',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: 1.6,
    whiteSpace: 'nowrap',
    // A qualifier, never a verdict: no health colour, so it cannot be misread
    // as a second score.
    cursor: 'default',
  },
  // Reachable by assistive tech, invisible on screen: this carries the chip's
  // accessible description without adding anything to the layout.
  // The Tooltip's direct child. It exists so our aria-describedby stays OFF the element MUI inspects:
  // MUI v4 sets the popper's id from the child's aria-describedby (Tooltip.js:540), so putting ours on
  // the child would give the popper the same id as the hidden description node.
  tooltipTarget: {
    display: 'inline-flex',
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

/**
 * The travelling qualifier on a pillar scored from only some of its areas
 * (feature 0037 / ADR-0022).
 *
 * It exists because the all-systems board renders a pillar number with no areas
 * and no room for a banner, and a Security score covering code alone would
 * otherwise be indistinguishable from one that also covers cloud posture — the
 * exact misreading feature 0025 was created to prevent. The same chip renders on
 * the Security tab so the board and the tab say the same thing.
 */
export const PartialScoreChip = ({
  reason,
}: {
  reason: PillarUnmeasuredReason;
}) => {
  const classes = useStyles();
  const explanation = EXPLANATION[reason];

  // React 18's useId, NOT the reason: the all-systems board renders one chip per row, and several
  // Systems commonly share the same partialReason (`no-production-accounts` is the common case). Keying
  // the id off `reason` produced duplicate DOM ids across rows — invalid HTML, and it makes
  // aria-describedby resolution browser-dependent, so most chips would silently lose the description
  // this component exists to provide.
  const descriptionId = useId();

  return (
    // The visible two words are the accessible NAME; the sentence is the accessible
    // DESCRIPTION, carried by a visually-hidden node the chip points at. An
    // aria-label would instead REPLACE the name with the whole sentence, so a
    // screen reader would announce the explanation twice and lose the short label.
    //
    // MUI v4's Tooltip has no `describeChild` (that is v5), hence doing it by hand
    // rather than letting the Tooltip own the description.
    <>
      <Tooltip title={explanation}>
        <span className={classes.tooltipTarget}>
          <span
            className={classes.chip}
            data-testid="partial-score-chip"
            data-reason={reason}
            aria-describedby={descriptionId}
          >
            {LABEL}
          </span>
        </span>
      </Tooltip>
      <span id={descriptionId} className={classes.srOnly}>
        {explanation}
      </span>
    </>
  );
};
